// /v2/settings/integrations — operator surface for the integrations
// framework (Phases 1-6 + correctness/observability pack).
//
// Vendor schemas come from GET /api/user/v1/integrations/kinds?include_schema=true.
// The page no longer maintains a per-vendor field map (that drifted out
// of sync with the adapter Joi schemas). The backend derives each
// vendor's UI shape directly from its Joi schema via
// lib/integrations/schema_introspect.js.
//
// Backend (all /api/user/v1/integrations/...):
//   GET    /kinds[?include_schema=true]                — supported vendors + UI schemas
//   GET    /                                           — destinations for this org
//   GET    /:id                                        — single destination
//   POST   /                                           — create
//   PUT    /:id                                        — update
//   DELETE /:id                                        — delete
//   POST   /:id/test                                   — synchronous test fire
//   GET    /:id/deliveries                             — recent delivery rows
//   POST   /:id/deliveries/:deliveryId/replay
//   GET    /dlq                                        — org-scoped DLQ jobs
//   POST   /dlq/:jobId/replay                          — DLQ replay
//   GET    /metrics                                    — admin metrics snapshot

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Typography,
  Button,
  Modal,
  Slide,
  Chip,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  CircularProgress,
  Alert,
  Tooltip,
  Drawer,
} from "@mui/material";
import {
  Trash2,
  Pencil,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  History,
} from "lucide-react";
import V2PageHeader, { pageDescriptionSx } from "../../../common/V2PageHeader";
import LayerdIcon from "../../../common/LayerdIcon";
import RefreshIconButton from "../../../common/RefreshIconButton";
import { UI_ICONS } from "../../../../icons/uiLucideIcons";
import PermissionGate from "../../../guards/PermissionGate";
import colors from "../../../../utils/colors";
import axios from "../../../../utils/axios/v1/userAxios";
import { IntegrationBrandIcon } from "./integrationBrandIcon";
import { SafeTextField, GreenSelectForm } from "../../../common/Greens";
import { validateDestinationForm } from "../../../../utils/validation";
import { SaveButton, formSaveButtonSx } from "../../../common/Button";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../super/design";

// ---------------------------------------------------------------------------
// Vendor field schemas
// ---------------------------------------------------------------------------
// Each entry describes the operator-visible config fields for one
// destination kind. The backend (lib/integrations/adapters/<kind>.js)
// owns the authoritative Joi schema; this map only renders the form.
//
// Field shape:
//   { key, label, type, required, placeholder, help, options }
// type: text | secret | url | select | bool | tags | textarea | json

const VENDOR_FIELDS = {
  pagerduty: {
    displayName: "PagerDuty",
    docUrl: "https://developer.pagerduty.com/docs/events-api-v2",
    fields: [
      { key: "routing_key", label: "Integration routing key", type: "secret", required: true,
        help: "32-character hex from Service → Integrations → Events API v2." },
      { key: "client_url",  label: "Client URL (optional)", type: "url",
        help: "Surface this URL in the PagerDuty incident details." },
    ],
  },
  slack_app: {
    displayName: "Slack (App / Bot)",
    docUrl: "https://api.slack.com/apps",
    fields: [
      { key: "bot_token",      label: "Bot user OAuth token", type: "secret", required: true,
        placeholder: "xoxb-…", help: "From OAuth & Permissions; needs chat:write." },
      { key: "default_channel", label: "Default channel", type: "text", required: true,
        placeholder: "#alerts", help: "Channel ID (recommended) or name." },
      { key: "signing_secret", label: "Signing secret (optional)", type: "secret",
        help: "Required only if you re-use this app to receive interactive callbacks." },
    ],
  },
  slack_webhook: {
    displayName: "Slack (Webhook URL)",
    docUrl: "https://api.slack.com/messaging/webhooks",
    fields: [
      { key: "webhook_url", label: "Incoming webhook URL", type: "secret", required: true,
        placeholder: "https://hooks.slack.com/services/…" },
    ],
  },
  opsgenie: {
    displayName: "Opsgenie",
    docUrl: "https://docs.opsgenie.com/docs/alert-api",
    fields: [
      { key: "api_key", label: "API key", type: "secret", required: true,
        help: "Integration → API → Read & Notify permissions." },
      { key: "region",  label: "Region", type: "select", required: true,
        options: [{ value: "us", label: "US" }, { value: "eu", label: "EU" }] },
      { key: "responders", label: "Responders (team/user names, comma-separated)", type: "tags",
        help: "Optional. Each entry is sent as { type:'team', name:<value> }." },
    ],
  },
  victorops: {
    displayName: "Splunk On-Call (VictorOps)",
    docUrl: "https://help.victorops.com/knowledge-base/rest-endpoint-integration-guide/",
    fields: [
      { key: "routing_key", label: "Routing key", type: "text", required: true,
        help: "1-128 characters from A-Z a-z 0-9 underscore or hyphen." },
      { key: "api_id",   label: "API ID (for retrieval)",  type: "text",
        help: "Optional. Used to look up the incident by entity_id." },
      { key: "api_key",  label: "API key (for retrieval)", type: "secret" },
    ],
  },
  firehydrant: {
    displayName: "FireHydrant",
    docUrl: "https://developers.firehydrant.io/docs/api/",
    fields: [
      { key: "bot_token",       label: "API bot token", type: "secret", required: true },
      { key: "environment_id",  label: "Environment ID", type: "text",
        help: "Optional. Tags signals with this environment." },
      { key: "infrastructure_id", label: "Infrastructure ID", type: "text",
        help: "Optional. Tags signals with this infrastructure." },
    ],
  },
  teams: {
    displayName: "Microsoft Teams",
    docUrl: "https://learn.microsoft.com/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook",
    fields: [
      { key: "webhook_url", label: "Incoming webhook URL", type: "secret", required: true },
    ],
  },
  discord: {
    displayName: "Discord",
    docUrl: "https://discord.com/developers/docs/resources/webhook",
    fields: [
      { key: "webhook_url",     label: "Webhook URL", type: "secret", required: true },
      { key: "mention_role_id", label: "Role to @mention on critical", type: "text" },
    ],
  },
  mattermost: {
    displayName: "Mattermost",
    docUrl: "https://developers.mattermost.com/integrate/webhooks/incoming/",
    fields: [
      { key: "webhook_url", label: "Incoming webhook URL", type: "secret", required: true },
      { key: "verify_tls",  label: "Verify TLS certificate", type: "bool",
        help: "Turn OFF only for self-hosted Mattermost with a private CA." },
    ],
  },
  webex: {
    displayName: "Webex",
    docUrl: "https://developer.webex.com/docs/api/v1/messages/create-a-message",
    fields: [
      { key: "bot_token", label: "Bot access token", type: "secret", required: true,
        placeholder: "Bot token from developer.webex.com" },
      { key: "room_id",   label: "Room ID", type: "text", required: true },
    ],
  },
  jira: {
    displayName: "Jira Cloud",
    docUrl: "https://developer.atlassian.com/cloud/jira/platform/rest/v3/",
    fields: [
      { key: "site_url",     label: "Jira site URL", type: "url", required: true,
        placeholder: "https://acme.atlassian.net" },
      { key: "email",        label: "Authenticating user email", type: "text", required: true },
      { key: "api_token",    label: "API token", type: "secret", required: true,
        help: "Generated at id.atlassian.com → Security → API tokens." },
      { key: "project_key",  label: "Project key", type: "text", required: true,
        placeholder: "OPS" },
      { key: "issue_type",   label: "Issue type", type: "text", required: true,
        placeholder: "Incident" },
      { key: "summary_prefix", label: "Summary prefix (optional)", type: "text" },
      { key: "custom_fields",  label: "Custom field map (JSON)", type: "json",
        help: 'e.g. {"customfield_10010":"Layerd","customfield_10011":"P1"}.' },
    ],
  },
  servicenow: {
    displayName: "ServiceNow",
    docUrl: "https://developer.servicenow.com/dev.do#!/reference/api/rome/rest/c_TableAPI",
    fields: [
      { key: "instance",   label: "Instance host", type: "text", required: true,
        placeholder: "acme.service-now.com" },
      { key: "auth_kind",  label: "Auth", type: "select", required: true,
        options: [
          { value: "basic", label: "Basic (user + password)" },
          { value: "token", label: "OAuth bearer token" },
        ] },
      { key: "username",     label: "Username", type: "text", help: "Only for Basic auth." },
      { key: "password",     label: "Password", type: "secret", help: "Only for Basic auth." },
      { key: "bearer_token", label: "Bearer token", type: "secret",
        help: "Only for OAuth bearer auth." },
      { key: "table",        label: "Target table", type: "text", required: true,
        placeholder: "incident", help: "Usually 'incident' or 'em_event'." },
      { key: "additional_fields", label: "Additional fields (JSON)", type: "json",
        help: 'Merged into the create payload. e.g. {"category":"network"}.' },
      { key: "search_fallback_minutes", label: "Duplicate-search window (minutes)", type: "text",
        help: "Defaults to 60. Looks up open records with the same correlation_id when the local idempotency cache misses." },
    ],
  },
  linear: {
    displayName: "Linear",
    docUrl: "https://developers.linear.app/docs/graphql/working-with-the-graphql-api",
    fields: [
      { key: "api_key",    label: "Personal API key", type: "secret", required: true,
        placeholder: "lin_api_…" },
      { key: "team_id",    label: "Team ID", type: "text", required: true },
      { key: "project_id", label: "Project ID", type: "text" },
      { key: "labels",     label: "Labels (comma-separated)", type: "tags" },
    ],
  },
  zendesk: {
    displayName: "Zendesk Support",
    docUrl: "https://developer.zendesk.com/api-reference/ticketing/tickets/tickets/",
    fields: [
      { key: "subdomain",       label: "Subdomain", type: "text", required: true,
        placeholder: "acme (from acme.zendesk.com)" },
      { key: "email",           label: "Authenticating user email", type: "text", required: true },
      { key: "api_token",       label: "API token", type: "secret", required: true,
        help: "Admin Center → Apps and integrations → API → Token access." },
      { key: "group_id",        label: "Group ID (numeric)", type: "text" },
      { key: "requester_email", label: "Requester email", type: "text",
        help: "Defaults to the authenticating email." },
      { key: "tags",            label: "Tags (comma-separated)", type: "tags" },
      { key: "custom_fields",   label: "Custom fields (JSON)", type: "json",
        help: '[{"id":12345,"value":"layerd"}]' },
    ],
  },
  freshdesk: {
    displayName: "Freshdesk",
    docUrl: "https://developers.freshdesk.com/api/",
    fields: [
      { key: "domain",        label: "Domain", type: "text", required: true,
        placeholder: "acme.freshdesk.com" },
      { key: "api_key",       label: "API key", type: "secret", required: true,
        help: "Profile → View API key." },
      { key: "group_id",      label: "Group ID (numeric)", type: "text" },
      { key: "product_id",    label: "Product ID (numeric)", type: "text" },
      { key: "status",        label: "Status code", type: "text",
        help: "Defaults to 2 (Open)." },
      { key: "priority",      label: "Priority code", type: "text",
        help: "Defaults to 2 (Medium)." },
      { key: "ticket_type",   label: "Ticket type", type: "text",
        placeholder: "Incident" },
      { key: "tags",          label: "Tags (comma-separated)", type: "tags" },
      { key: "requester_id",  label: "Requester ID (numeric)", type: "text" },
      { key: "custom_fields", label: "Custom fields (JSON)", type: "json" },
    ],
  },
  datadog: {
    displayName: "Datadog (Events)",
    docUrl: "https://docs.datadoghq.com/api/latest/events/",
    fields: [
      { key: "api_key", label: "API key", type: "secret", required: true,
        help: "32-character hex. From Organization Settings → API keys." },
      { key: "site",    label: "Datadog site", type: "select", required: true,
        options: [
          { value: "us1",     label: "US1 (datadoghq.com)" },
          { value: "us3",     label: "US3 (us3.datadoghq.com)" },
          { value: "us5",     label: "US5 (us5.datadoghq.com)" },
          { value: "eu1",     label: "EU1 (datadoghq.eu)" },
          { value: "ap1",     label: "AP1 (ap1.datadoghq.com)" },
          { value: "us1_fed", label: "US1-FED (ddog-gov.com)" },
        ] },
      { key: "app_key", label: "Application key (optional)", type: "secret",
        help: "40-character hex. Reserved for the upcoming APM workstream." },
      { key: "service_tag", label: "service tag", type: "text",
        placeholder: "layerd", help: "Defaults to 'layerd'." },
      { key: "env",         label: "env tag", type: "text", placeholder: "prod" },
      { key: "default_tags", label: "Default tags (comma-separated)", type: "tags",
        help: "e.g. team:secops, region:us-east-1" },
      { key: "dashboard_base_url", label: "Dashboard base URL (optional)", type: "url",
        help: "Surfaced as a link in delivered events." },
    ],
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function vendorMeta(kind) {
  return VENDOR_FIELDS[kind] || {
    displayName: kind,
    docUrl: null,
    fields: [],
  };
}

function emptyConfigForFields(fields) {
  const out = {};
  for (const f of fields || []) {
    if (f.type === "tags") out[f.key] = [];
    else if (f.type === "bool") out[f.key] = f.default !== undefined ? f.default : false;
    else if (f.type === "json") out[f.key] = "";
    else if (f.type === "number") out[f.key] = f.default !== undefined ? String(f.default) : "";
    else out[f.key] = f.default !== undefined ? String(f.default) : "";
  }
  return out;
}

// Convert form state → adapter payload. Strips empties, parses JSON
// fields, coerces numbers. Throws on malformed JSON / non-numeric input
// so the save handler can show the error inline.
function buildConfigPayload(fields, formConfig) {
  const out = {};
  for (const f of fields || []) {
    const raw = formConfig[f.key];
    if (raw === undefined || raw === null) continue;
    if (f.type === "json") {
      if (raw === "" || raw === "{}" || raw === "[]") continue;
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        throw new Error(`${f.label}: invalid JSON (${e.message})`);
      }
      out[f.key] = parsed;
    } else if (f.type === "tags") {
      if (Array.isArray(raw) && raw.length > 0) out[f.key] = raw;
    } else if (f.type === "bool") {
      out[f.key] = Boolean(raw);
    } else if (f.type === "number") {
      if (raw === "" || raw === null) continue;
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        throw new Error(`${f.label}: must be a number`);
      }
      out[f.key] = n;
    } else if (f.type === "secret") {
      // On edit, blank means "keep existing" — don't send.
      if (typeof raw === "string" && raw.length > 0) out[f.key] = raw;
    } else if (typeof raw === "string") {
      if (raw.length > 0) out[f.key] = raw;
    } else {
      out[f.key] = raw;
    }
  }
  return out;
}

function statusChip(status) {
  if (status === "ok" || status === "delivered") {
    return <Chip size="small" icon={<CheckCircle2 size={12} />} label="OK"
      sx={{ bgcolor: "#e6f7e8", color: "#1c7c2f", fontWeight: 500, height: 22 }} />;
  }
  if (status === "failed" || status === "permanent_failure") {
    return <Chip size="small" icon={<XCircle size={12} />} label="Failed"
      sx={{ bgcolor: "#fdecea", color: "#b3261e", fontWeight: 500, height: 22 }} />;
  }
  if (status === "retryable") {
    return <Chip size="small" icon={<Clock size={12} />} label="Retrying"
      sx={{ bgcolor: "#fff4e0", color: "#a25b00", fontWeight: 500, height: 22 }} />;
  }
  if (status === "skipped") {
    return <Chip size="small" label="Skipped"
      sx={{ bgcolor: "#f0f0f4", color: "#666", fontWeight: 500, height: 22 }} />;
  }
  if (status === "dlq") {
    return <Chip size="small" icon={<XCircle size={12} />} label="DLQ"
      sx={{ bgcolor: "#fdecea", color: "#811818", fontWeight: 500, height: 22 }} />;
  }
  return <Chip size="small" label={status || "—"}
    sx={{ bgcolor: "#f0f0f4", color: "#444", height: 22 }} />;
}

function timeAgo(date) {
  if (!date) return "—";
  const d = new Date(date);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

// ---------------------------------------------------------------------------
// Shared integration drawer form styles (matches webhook config drawer)
// ---------------------------------------------------------------------------

const integrationPanelSx = {
  p: 3,
  borderRadius: "12px",
  backgroundColor: colors.neutral[50],
  border: `1px solid ${colors.neutral[200]}`,
};

function IntegrationStatusSwitch({ checked, onChange }) {
  return (
    <Box
      onClick={() => onChange(!checked)}
      sx={{
        width: 48,
        height: 26,
        borderRadius: "13px",
        backgroundColor: checked ? "#6371F7" : colors.neutral[300],
        position: "relative",
        cursor: "pointer",
        transition: "background-color 0.2s",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: "#fff",
          position: "absolute",
          top: 3,
          left: checked ? 25 : 3,
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </Box>
  );
}

function FieldHelp({ children }) {
  if (!children) return null;
  return (
    <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[500] }}>
      {children}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// Per-field renderer
// ---------------------------------------------------------------------------

function FieldRenderer({ field, value, onChange, isEdit, error, onBlur }) {
  if (field.type === "select") {
    return (
      <GreenSelectForm variant="outlined" size="small" fullWidth required={field.required} error={Boolean(error)}>
        <InputLabel required={field.required}>{field.label}</InputLabel>
        <Select
          value={value || ""}
          label={field.label}
          onChange={(e) => onChange(e.target.value)}
        >
          {field.options.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
        {error ? (
          <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.red?.[500] || "#d32f2f", mt: 0.75, mx: 1.75 }}>
            {error}
          </Typography>
        ) : field.help ? (
          <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[500], mt: 0.75, mx: 1.75 }}>
            {field.help}
          </Typography>
        ) : null}
      </GreenSelectForm>
    );
  }
  if (field.type === "bool") {
    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: "medium", color: colors.neutral[1000] }}>
            {field.label}
          </Typography>
          <FieldHelp>{field.help}</FieldHelp>
        </Stack>
        <IntegrationStatusSwitch checked={Boolean(value)} onChange={onChange} />
      </Stack>
    );
  }

  const isSecret = field.type === "secret";
  const isJson = field.type === "json";
  const sanitizeField = !["url", "email", "secret", "json", "number"].includes(field.type);
  const placeholder = isSecret && isEdit ? "Leave blank to keep existing" : field.placeholder;
  const csv = field.type === "tags"
    ? (Array.isArray(value) ? value.join(", ") : (value || ""))
    : null;

  return (
    <SafeTextField
      sanitize={sanitizeField}
      label={field.label}
      size="small"
      fullWidth
      required={field.required}
      error={Boolean(error)}
      helperText={error || field.help}
      multiline={isJson}
      minRows={isJson ? 3 : 1}
      type={isSecret ? "password" : "text"}
      value={field.type === "tags" ? csv : (value || "")}
      placeholder={placeholder}
      onBlur={onBlur}
      onChange={(e) => {
        if (field.type === "tags") {
          onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean));
          return;
        }
        onChange(e.target.value);
      }}
      sx={isJson || isSecret ? { "& input, & textarea": { fontFamily: "monospace" } } : undefined}
    />
  );
}

// ---------------------------------------------------------------------------
// Destination editor (create + edit)
// ---------------------------------------------------------------------------

function DestinationEditor({ open, onClose, onSaved, mode, kind, existing }) {
  const isEdit = mode === "edit";
  const effectiveKind = isEdit && existing ? existing.kind : kind;
  const meta = vendorMeta(effectiveKind);
  const title = isEdit ? `Edit ${meta.displayName}` : `New ${meta.displayName}`;

  const initial = useMemo(() => {
    if (isEdit && existing) {
      return { name: existing.name, enabled: existing.enabled, config: emptyConfigForFields(meta.fields) };
    }
    return { name: "", enabled: true, config: emptyConfigForFields(meta.fields) };
  }, [isEdit, existing, effectiveKind, meta]);

  const [edit, setEdit] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [baselineKey, setBaselineKey] = useState(0);

  useEffect(() => {
    setEdit(initial);
    setErr(null);
    setTouched({});
    setSubmitAttempted(false);
    if (open) setBaselineKey((k) => k + 1);
  }, [initial, open]);

  const validation = useMemo(
    () => validateDestinationForm(meta, edit, { isEdit }),
    [meta, edit, isEdit],
  );

  const showFormErr = (field) => (submitAttempted || touched[field]) && validation.formErrors[field];
  const showFieldErr = (key) => (submitAttempted || touched[key]) && validation.fieldErrors[key];

  const formSnapshot = useMemo(() => edit, [edit]);
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(onClose, isDirty, { disabled: saving });

  const save = async () => {
    setSubmitAttempted(true);
    const result = validateDestinationForm(meta, edit, { isEdit });
    if (!result.valid) return;

    setErr(null);
    setSaving(true);
    try {
      let configPayload;
      try {
        configPayload = buildConfigPayload(meta.fields, edit.config);
      } catch (e) {
        throw new Error(e.message);
      }
      const payload = {
        name: edit.name,
        enabled: edit.enabled,
        config: configPayload,
      };
      if (isEdit) {
        const r = await axios.put(`integrations/${existing.id}`, payload);
        onSaved(r.data.item, true);
      } else {
        payload.kind = effectiveKind;
        const r = await axios.post(`integrations`, payload);
        onSaved(r.data.item, false);
      }
    } catch (e) {
      setErr(e.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Modal open={open} onClose={requestClose}>
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Box sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "33%",
          minWidth: 480,
          height: "100vh",
          bgcolor: "#fff",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
          zIndex: 1300,
          borderTopLeftRadius: "8px",
          outline: "none",
          display: "flex",
          flexDirection: "column",
        }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 4, borderBottom: `1px solid ${colors.neutral[200]}` }}
          >
            <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1, overflow: "visible" }}>
              <IntegrationBrandIcon id={effectiveKind} size={48} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 20, fontWeight: "medium", color: colors.neutral[1000] }}>
                  {title}
                </Typography>
                <Typography sx={{ ...pageDescriptionSx, mt: 0.5, maxWidth: "none" }}>
                  {isEdit
                    ? "Update name, toggle, or replace credentials."
                    : "Credentials are encrypted at rest with a per-org key."}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={requestClose} aria-label="Close">
              <LayerdIcon icon={UI_ICONS.close} size={24} color="neutral" />
            </IconButton>
          </Stack>

          <Box sx={{ flex: 1, overflowY: "auto", p: 4 }}>
            <Stack spacing={4}>
              {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}

              <Box sx={integrationPanelSx}>
                <Stack spacing={3}>
                  <SafeTextField
                    label="Name"
                    size="small"
                    fullWidth
                    required
                    value={edit.name || ""}
                    placeholder="Production alerts"
                    error={Boolean(showFormErr("name"))}
                    helperText={showFormErr("name") || "Operator-facing label. Unique per vendor within this org."}
                    onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
                    onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                  />

                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: "medium", color: colors.neutral[1000] }}>
                        Enabled
                      </Typography>
                      <FieldHelp>Disable to pause this destination without deleting it.</FieldHelp>
                    </Stack>
                    <IntegrationStatusSwitch
                      checked={Boolean(edit.enabled)}
                      onChange={(checked) => setEdit({ ...edit, enabled: checked })}
                    />
                  </Stack>
                </Stack>
              </Box>

              <Box sx={integrationPanelSx}>
                <Typography sx={{ fontSize: 14, fontWeight: "medium", color: colors.neutral[1000], mb: 3 }}>
                  Credentials &amp; options
                </Typography>
                <Stack spacing={3}>
                  {meta.fields.map((f) => (
                    <FieldRenderer
                      key={f.key}
                      field={f}
                      isEdit={isEdit}
                      value={edit.config[f.key]}
                      error={showFieldErr(f.key)}
                      onBlur={() => setTouched((prev) => ({ ...prev, [f.key]: true }))}
                      onChange={(v) => setEdit({ ...edit, config: { ...edit.config, [f.key]: v } })}
                    />
                  ))}
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 2.5,
                  borderRadius: "8px",
                  backgroundColor: "rgba(99, 113, 247, 0.08)",
                  border: "1px solid rgba(99, 113, 247, 0.2)",
                }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <LayerdIcon icon={UI_ICONS.info} size={20} color="primary" style={{ flexShrink: 0, marginTop: 2 }} />
                  <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: "#6371F7" }}>
                    Alert events routed to this destination are delivered using the credentials above.
                    Use a test fire after saving to confirm connectivity.
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Box>

          <Stack
            direction="row"
            spacing={2}
            sx={{ p: 4, borderTop: `1px solid ${colors.neutral[200]}` }}
          >
            <Button
              onClick={requestClose}
              disabled={saving}
              sx={{
                height: 44,
                flex: 1,
                borderRadius: "8px",
                fontSize: 14,
                fontWeight: "medium",
                textTransform: "none",
                color: colors.neutral[700],
                border: `1px solid ${colors.neutral[300]}`,
                backgroundColor: "#fff",
                "&:hover": {
                  backgroundColor: colors.neutral[50],
                  borderColor: colors.neutral[400],
                },
              }}
            >
              Cancel
            </Button>
            <SaveButton
              onClick={save}
              disabled={saving || !validation.valid}
              loading={saving}
              label={saving ? "Saving..." : (isEdit ? "Save changes" : "Create destination")}
              sx={{ ...formSaveButtonSx, flex: 1, width: "auto" }}
            />
          </Stack>
        </Box>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
}

// ---------------------------------------------------------------------------
// Delivery drawer
// ---------------------------------------------------------------------------

function DeliveryDrawer({ open, onClose, destination }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [replaying, setReplaying] = useState({});

  const load = useCallback(async () => {
    if (!destination) return;
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`integrations/${destination.id}/deliveries?limit=100`);
      setRows(r.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [destination]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const replay = async (deliveryId) => {
    setReplaying((s) => ({ ...s, [deliveryId]: true }));
    try {
      await axios.post(`integrations/${destination.id}/deliveries/${deliveryId}/replay`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setReplaying((s) => ({ ...s, [deliveryId]: false }));
    }
  };

  const meta = destination ? vendorMeta(destination.kind) : null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: "44%", minWidth: 580 } }}>
      <Box sx={{ p: 3, borderBottom: "1px solid #eee" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Delivery history</Typography>
            <Typography variant="caption" sx={{ color: colors.neutral[700] }}>
              {destination ? `${vendorMeta(destination.kind).displayName} → ${destination.name}` : ""}
            </Typography>
          </Box>
          <RefreshIconButton onClick={load} disabled={loading} loading={loading} size="compact" />
        </Stack>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!loading && rows.length === 0 && (
          <Typography variant="body2" sx={{ color: colors.neutral[700], p: 2 }}>
            No deliveries yet. Fire a test or trigger a real alert.
          </Typography>
        )}
        <Stack spacing={1}>
          {rows.map((d) => {
            const isFail = d.status === "permanent_failure" || d.status === "dlq";
            return (
              <Box key={d.id}
                sx={{ p: 1.5, border: "1px solid #ececf0", borderRadius: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {statusChip(d.status)}
                    {d.isTestFire && (
                      <Chip size="small" label="Test fire"
                        sx={{ bgcolor: "#eef0fc", color: "#4759b8", height: 22 }} />
                    )}
                    <Typography variant="caption" sx={{ color: colors.neutral[700] }}>
                      {timeAgo(d.createdAt)} · attempt {d.attempt}
                      {typeof d.httpCode === "number" ? ` · HTTP ${d.httpCode}` : ""}
                      {typeof d.durationMs === "number" ? ` · ${d.durationMs}ms` : ""}
                    </Typography>
                  </Stack>
                  {isFail && !d.isTestFire && d.alertEventId && (
                    <Button size="small" startIcon={<Play size={12} />}
                      onClick={() => replay(d.id)} disabled={Boolean(replaying[d.id])}>
                      {replaying[d.id] ? "…" : "Replay"}
                    </Button>
                  )}
                </Stack>
                {d.vendorEntityId && (
                  <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: colors.neutral[700] }}>
                    vendor id: <code>{d.vendorEntityId}</code>
                  </Typography>
                )}
                {d.vendorResponse && (
                  <Box sx={{ mt: 0.5, p: 1, bgcolor: "#fafafc", borderRadius: 0.5,
                    fontFamily: "ui-monospace, monospace", fontSize: 11, color: "#444",
                    whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 120, overflowY: "auto" }}>
                    {d.vendorResponse}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Shared alert-integration UI
// ---------------------------------------------------------------------------

export function useAlertIntegrations() {
  const [kinds, setKinds] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editor, setEditor] = useState(null);
  const [deliveriesFor, setDeliveriesFor] = useState(null);
  const [testing, setTesting] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [kindsR, listR] = await Promise.all([
        axios.get("integrations/kinds?include_schema=true"),
        axios.get("integrations"),
      ]);
      setKinds(kindsR.data.kinds || []);
      setItems(listR.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const destinationsForKind = useCallback((kind) => (
    items
      .filter((i) => i.kind === kind)
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
  ), [items]);

  const onSaved = (item, isEdit) => {
    setItems((cur) => {
      if (isEdit) return cur.map((i) => (i.id === item.id ? item : i));
      return [...cur, item];
    });
    setEditor(null);
  };

  const openVendor = (kind) => {
    const existing = destinationsForKind(kind)[0];
    setEditor(existing
      ? { mode: "edit", kind, existing }
      : { mode: "create", kind, existing: null });
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Delete destination "${item.name}"? Alerts targeting it will stop delivering.`)) return;
    try {
      await axios.delete(`integrations/${item.id}`);
      setItems((cur) => cur.filter((i) => i.id !== item.id));
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    }
  };

  const onTest = async (item) => {
    setTesting((s) => ({ ...s, [item.id]: true }));
    try {
      const r = await axios.post(`integrations/${item.id}/test`);
      const ok = r.data && r.data.ok;
      alert(ok ? `Test delivered (${r.data.latencyMs || 0}ms).` : `Test failed: ${r.data?.summary || "unknown"}`);
      load();
    } catch (e) {
      alert(e.response?.data?.message || e.message);
    } finally {
      setTesting((s) => ({ ...s, [item.id]: false }));
    }
  };

  return {
    kinds,
    items,
    loading,
    err,
    load,
    editor,
    setEditor,
    onSaved,
    openVendor,
    destinationsForKind,
    deliveriesFor,
    setDeliveriesFor,
    onDelete,
    onTest,
    testing,
  };
}

export function AlertVendorCard({ kind, displayName, configured, enabled, onClick }) {
  const statusLabel = configured ? (enabled ? "Enabled" : "Disabled") : "Not configured";
  const statusColors = configured
    ? (enabled
      ? { bg: "#D1FAE5", color: "#065F46" }
      : { bg: "#FEF3C7", color: "#92400E" })
    : { bg: colors.neutral[100], color: colors.neutral[700] };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: "12px",
        backgroundColor: "#fff",
        border: `1px solid ${colors.neutral[200]}`,
        transition: "all 0.2s",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 140,
      }}
    >
      <Box
        onClick={onClick}
        sx={{
          cursor: "pointer",
          transition: "transform 0.2s",
          "&:hover": { transform: "scale(1.05)" },
        }}
      >
        <IntegrationBrandIcon id={kind} size={48} />
      </Box>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: "medium",
          color: colors.neutral[1000],
          mt: 2,
          textAlign: "center",
        }}
      >
        {displayName}
      </Typography>
      <Chip
        label={statusLabel}
        size="small"
        onClick={onClick}
        sx={{
          mt: 1.5,
          height: 24,
          backgroundColor: statusColors.bg,
          color: statusColors.color,
          fontSize: 11,
          fontWeight: "medium",
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": { opacity: 0.8, transform: "scale(1.05)" },
        }}
      />
    </Box>
  );
}

export function ConfiguredDestinationsList({
  items,
  testing,
  onTest,
  onEdit,
  onDelete,
  onDeliveries,
}) {
  if (!items.length) return null;

  return (
    <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${colors.neutral[200]}` }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.neutral[1000], mb: 2 }}>
        Configured alert destinations
      </Typography>
      <Stack spacing={1}>
        {items.map((it) => {
          const meta = vendorMeta(it.kind);
          return (
            <Box key={it.id} sx={{ p: 1.75, border: "1px solid #e6e6ed", borderRadius: 1, bgcolor: "#fff" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                  <IntegrationBrandIcon id={it.kind} size={36} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" fontWeight={600} noWrap>{it.name}</Typography>
                      <Chip size="small" label={meta.displayName}
                        sx={{ height: 20, bgcolor: "#eef0fc", color: "#4759b8", fontSize: 11 }} />
                      {!it.enabled && (
                        <Chip size="small" label="Disabled"
                          sx={{ height: 20, bgcolor: "#f0f0f4", color: "#777", fontSize: 11 }} />
                      )}
                      {it.testStatus === "ok" && (
                        <Tooltip title={`Last test ${timeAgo(it.testLastAt)}`}>
                          <Chip size="small" icon={<CheckCircle2 size={11} />} label="Test OK"
                            sx={{ height: 20, bgcolor: "#e6f7e8", color: "#1c7c2f", fontSize: 11 }} />
                        </Tooltip>
                      )}
                      {it.testStatus === "failed" && (
                        <Tooltip title={it.testLastError || "Last test failed"}>
                          <Chip size="small" icon={<XCircle size={11} />} label="Test failed"
                            sx={{ height: 20, bgcolor: "#fdecea", color: "#b3261e", fontSize: 11 }} />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="caption" sx={{ color: colors.neutral[700] }}>
                      Created {timeAgo(it.createdAt)} · Updated {timeAgo(it.updatedAt)}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Send a test event">
                    <span>
                      <IconButton size="small" onClick={() => onTest(it)} disabled={Boolean(testing[it.id])}>
                        {testing[it.id] ? <CircularProgress size={14} /> : <Play size={14} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delivery history">
                    <IconButton size="small" onClick={() => onDeliveries(it)}>
                      <History size={14} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(it)}>
                      <Pencil size={14} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => onDelete(it)}>
                      <Trash2 size={14} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

export function AlertIntegrationOverlays({
  editor,
  setEditor,
  onSaved,
  deliveriesFor,
  setDeliveriesFor,
}) {
  return (
    <>
      {editor && (
        <DestinationEditor
          open={Boolean(editor)}
          mode={editor.mode}
          kind={editor.kind}
          existing={editor.existing}
          onClose={() => setEditor(null)}
          onSaved={onSaved}
        />
      )}
      <DeliveryDrawer
        open={Boolean(deliveriesFor)}
        destination={deliveriesFor}
        onClose={() => setDeliveriesFor(null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page (legacy standalone route)
// ---------------------------------------------------------------------------

export function AlertIntegrationsPanel({ embedded = false } = {}) {
  const alert = useAlertIntegrations();

  const content = (
    <>
      <Box sx={{ p: embedded ? 0 : 3 }}>
        {alert.err && <Alert severity="error" sx={{ mb: 2 }}>{alert.err}</Alert>}
        <ConfiguredDestinationsList
          items={alert.items}
          testing={alert.testing}
          onTest={alert.onTest}
          onEdit={(it) => alert.setEditor({ mode: "edit", kind: it.kind, existing: it })}
          onDelete={alert.onDelete}
          onDeliveries={alert.setDeliveriesFor}
        />
      </Box>
      <AlertIntegrationOverlays {...alert} />
    </>
  );

  if (embedded) {
    return (
      <PermissionGate permission="ALERT">
        {content}
      </PermissionGate>
    );
  }

  return (
    <PermissionGate permission="ALERT">
      <Helmet><title>Integrations · Layerd</title></Helmet>
      <V2PageHeader
        title="Integrations"
        description="Destinations that receive alert events from this organisation."
      />
      {content}
    </PermissionGate>
  );
}

export default function IntegrationsPage() {
  return <AlertIntegrationsPanel />;
}
